import React, {
  useCallback, useEffect, useRef
} from 'react'
import { useQuery } from '@apollo/react-hooks'
import { useDispatch, useSelector } from 'react-redux'
import { Route } from 'react-router-dom'

import {
  castSenderConnected,
  castSenderDisconnected,
  updateAuthTokenAndDefaultTheme as updateAuthTokenAndDefaultThemeAction,
  setManageSubsOptions
} from '../../actions'
import Head from '../../head'
import Routes from '../../routes'
import LoadingOverlay from '../loading-overlay'
import ServiceMessages from '../service-messages'
import PlanUpgradePopup from '../modals/plan-upgrade-popup/plan-upgrade-popup'
import initCastSender from '../../lib/cast-sender'
import ACCOUNT_QUERY from '../../../graphql/queries/account.gql'

import { initYouboraPlugin } from '../../lib/player'
import { getAccountIdFromConfig } from '../../selectors/config'
import {
  getTokenFromState,
  getAccountIdFromState
} from '../../selectors/session'
import { initDomPurify } from '../../utils'
import { segmentIdentify } from '../../segment/segment-identify'
import { STORAGE_KEY_PARENTAL_RATINGS } from '../../storage-keys'
import { readyToReset as readyToResetAction } from '../../auth/actions'
import { getIsResetting } from '../../selectors/auth'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import { useConfig } from '../../hooks/use-config'

const EMPTY_MANAGE_SUBS_OPTIONS = []

const Main = () => {
  const dispatch = useDispatch()
  const prevManageSubsOptionsRef = useRef()
  // Run app initialisers once after config and account data are ready.
  const hasInitializedRef = useRef(false)

  const accountId = useSelector(getAccountIdFromState)
  const theme = useSelector(state => state.theme)
  const token = useSelector(getTokenFromState)
  const isAuthenticated = useIsAuthenticated()

  // isResetting means auth is waiting to clear the Apollo cache.
  // Signal readiness whenever auth is waiting to reset Apollo.
  const isResetting = useSelector(getIsResetting)

  const updateCastSenderStatus = useCallback(connected => {
    if (!connected) {
      dispatch(castSenderDisconnected())
      return
    }
    dispatch(castSenderConnected())
  }, [dispatch])

  const updateAuthTokenAndDefaultTheme = useCallback((authToken, defaultTheme) => {
    dispatch(updateAuthTokenAndDefaultThemeAction(authToken, defaultTheme))
  }, [dispatch])

  const updateManageSubsOptions = useCallback((manageSubsOptions) => {
    dispatch(setManageSubsOptions(manageSubsOptions))
  }, [dispatch])

  useEffect(() => {
    if (isResetting) {
      dispatch(readyToResetAction())
    }
  }, [dispatch, isResetting])

  const {
    appConfig,
    parentalRatingsData: parentalRatings,
    configLoading,
    deviceFingerprint,
    deviceInfo
  } = useConfig('network-only')

  const configAccountId = getAccountIdFromConfig(appConfig)

  const {
    loading: accountLoading,
    data: accountData
  } = useQuery(ACCOUNT_QUERY, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-first'
  })

  const account = isAuthenticated && accountData ? accountData.account : null
  const isAccountLoading = isAuthenticated && accountLoading
  const manageSubsOptions = account?.subscription?.manageSubsOptions || EMPTY_MANAGE_SUBS_OPTIONS
  const configSessionToken = appConfig?.session?.token
  const hasNewConfigToken = configSessionToken && token !== configSessionToken
  const shouldUpdateAuthToken = hasNewConfigToken && (
    (!isAuthenticated && !configAccountId) ||
    (isAuthenticated && configAccountId && configAccountId !== accountId)
  )

  // Update the store with guest tokens only when logged out, and account tokens only when logged in.
  useEffect(() => {
    if (shouldUpdateAuthToken) {
      updateAuthTokenAndDefaultTheme(configSessionToken, theme)
    }
  }, [
    configSessionToken,
    shouldUpdateAuthToken,
    theme,
    updateAuthTokenAndDefaultTheme
  ])

  // Run the one-off setup that needs config, account and device details.
  useEffect(() => {
    if (
      hasInitializedRef.current ||
      !deviceFingerprint ||
      !appConfig?.values ||
      configLoading ||
      isAccountLoading
    ) {
      return
    }

    hasInitializedRef.current = true

    localStorage.setItem(STORAGE_KEY_PARENTAL_RATINGS, JSON.stringify(parentalRatings))

    if (manageSubsOptions.length) updateManageSubsOptions(manageSubsOptions)

    initYouboraPlugin(appConfig)
    initDomPurify(appConfig)
    initCastSender({
      appConfig,
      deviceInfo,
      updateCastSenderStatus
    })
  }, [
    appConfig,
    configLoading,
    deviceFingerprint,
    deviceInfo,
    isAccountLoading,
    manageSubsOptions,
    parentalRatings,
    updateCastSenderStatus,
    updateManageSubsOptions
  ])

  // Update stored subscription management options when the account query changes.
  useEffect(() => {
    const prevManageSubsOptions = prevManageSubsOptionsRef.current

    prevManageSubsOptionsRef.current = manageSubsOptions

    if (
      prevManageSubsOptions !== undefined &&
      manageSubsOptions !== prevManageSubsOptions
    ) {
      updateManageSubsOptions(manageSubsOptions)
    }
  }, [manageSubsOptions, updateManageSubsOptions])

  // Identify the signed-in account for Segment whenever account data changes.
  useEffect(() => {
    if (account) {
      const currentProfile = account.profiles?.find(item => item.id === account.selectedProfile)
      segmentIdentify(account, currentProfile, true)
    }
  }, [account])

  if (
    isResetting ||
    !deviceFingerprint ||
    configLoading ||
    isAccountLoading ||
    shouldUpdateAuthToken
  ) {
    return null
  }

  return (
    <div>
      <Head />
      <LoadingOverlay />
      <ServiceMessages />
      <PlanUpgradePopup appConfig={appConfig} />
      <Route
        render={routeProps => (
          <Routes {...routeProps} />
        )}
      />
    </div>
  )
}

export default React.memo(Main)
